class FreeGiftController {
  constructor(cartAPI, configuration = {}) {
    this.cartAPI = cartAPI;
    this.unavailableVariantIds = new Set();
    this.reconciliation = null;
    this.updateConfiguration(configuration);
  }

  updateConfiguration(configuration = {}) {
    const previousSignature = JSON.stringify((this.configuration?.tiers || []).map((tier) => tier.freeVariantId));
    this.configuration = configuration;
    this.tiers = this.validateTiers(configuration.tiers || []);
    const nextSignature = JSON.stringify(this.tiers.map((tier) => tier.freeVariantId));

    if (previousSignature !== nextSignature) this.unavailableVariantIds.clear();
    this.logConfiguration();
  }

  validateTiers(tiers) {
    const enabledTiers = tiers
      .map((tier, index) => ({
        ...tier,
        index,
        key: String(tier.key || index + 1),
        setup: tier.setup === 'single_variant' ? 'single_variant' : 'two_variants',
        thresholdCents: Number(tier.thresholdCents),
        productId: Number(tier.productId),
        variantCount: Number(tier.variantCount),
        freeVariantId: Number(tier.freeVariantId),
        paidVariantId: Number(tier.paidVariantId),
        freeVariantPrice: Number(tier.freeVariantPrice),
        paidVariantPrice: Number(tier.paidVariantPrice)
      }))
      .filter((tier) => tier.enabled);

    const freeVariantCounts = enabledTiers.reduce((counts, tier) => {
      if (tier.freeVariantId > 0) counts[tier.freeVariantId] = (counts[tier.freeVariantId] || 0) + 1;
      return counts;
    }, {});

    return enabledTiers.map((tier) => {
      const errors = Array.isArray(tier.errors) ? tier.errors.filter(Boolean) : [];
      if (!(tier.thresholdCents > 0)) errors.push('threshold');
      if (!(tier.productId > 0)) errors.push('product');
      if (!(tier.freeVariantId > 0)) errors.push('free_variant');
      if (tier.freeVariantPrice !== 0 && !this.configuration.debug) errors.push('free_variant_price');
      if (tier.setup === 'single_variant') {
        if (tier.variantCount !== 1 && !this.configuration.debug) errors.push('variant_count');
      } else {
        if (tier.variantCount !== 2) errors.push('variant_count');
        if (!(tier.paidVariantId > 0)) errors.push('paid_variant');
        if (tier.freeVariantId === tier.paidVariantId) errors.push('matching_variants');
        if (!(tier.paidVariantPrice > 0)) errors.push('paid_variant_price');
      }
      if (freeVariantCounts[tier.freeVariantId] > 1) errors.push('duplicate_free_variant');
      return { ...tier, errors: [...new Set(errors)], valid: errors.length === 0 };
    });
  }

  calculateQualifyingSubtotal(cart) {
    const configuredFreeVariantIds = new Set(
      (this.configuration.tiers || [])
        .filter((tier) => Number(tier.freeVariantPrice) === 0)
        .map((tier) => Number(tier.freeVariantId))
        .filter((id) => id > 0)
    );

    return (cart.items || []).reduce((subtotal, item) => {
      const properties = item.properties || {};
      const isCampaignGift = properties._free_gift === 'true' &&
        properties._gift_campaign === this.configuration.campaignKey;

      if (item.gift_card || isCampaignGift || configuredFreeVariantIds.has(Number(item.variant_id))) {
        return subtotal;
      }

      /*
       * Ajax Cart `final_line_price` is the merchandise line value after selling-plan
       * and line-level discounts, but before cart-level discount allocations. It is
       * therefore the actual qualifying selling value and remains stable when an
       * order-level discount code is applied. `original_line_price` would incorrectly
       * restore product-specific discounts; `cart.total_price` would include cart-level
       * discounts and could revoke an already-earned gift.
       */
      const lineValue = Number.isFinite(Number(item.final_line_price))
        ? Number(item.final_line_price)
        : Number(item.line_price || 0);

      return subtotal + Math.max(0, lineValue);
    }, 0);
  }

  logConfiguration() {
    const invalidTiers = this.tiers.filter((tier) => !tier.valid);
    const rows = this.tiers.map((tier) => ({
      tier: tier.key,
      title: tier.title,
      setup: tier.setup,
      valid: tier.valid,
      threshold: tier.thresholdCents,
      product: tier.productTitle || tier.productId,
      variants: tier.variantCount,
      freeVariantId: tier.freeVariantId,
      freePrice: tier.freeVariantPrice,
      paidVariantId: tier.paidVariantId,
      paidPrice: tier.paidVariantPrice,
      paidAvailable: tier.paidAvailable,
      debugPaidTest: Boolean(this.configuration.debug && tier.freeVariantPrice > 0),
      available: tier.available,
      errors: tier.errors.map((error) => this.describeError(error, tier)).join(' | ')
    }));

    if (this.configuration.debug) {
      console.groupCollapsed('[Free gifts] Configuration');
      console.info('Campaign settings', {
        enabled: this.configuration.enabled,
        mode: this.configuration.mode,
        autoAdd: this.configuration.autoAdd,
        outOfStockBehavior: this.configuration.outOfStockBehavior
      });
      console.table(rows);
      console.groupEnd();
    }

    if (invalidTiers.length) {
      console.warn('[Free gifts] Invalid enabled tier(s); they cannot be added.', rows.filter((row) => !row.valid));
    }
  }

  logState(label, cart, state) {
    if (!this.configuration.debug) return;
    const configuredFreeVariantIds = new Set(
      (this.configuration.tiers || [])
        .filter((tier) => Number(tier.freeVariantPrice) === 0)
        .map((tier) => Number(tier.freeVariantId))
        .filter((id) => id > 0)
    );
    const items = (cart.items || []).map((item) => {
      const properties = item.properties || {};
      let qualification = 'included';
      if (item.gift_card) qualification = 'excluded: gift card';
      else if (properties._free_gift === 'true' && properties._gift_campaign === this.configuration.campaignKey) {
        qualification = 'excluded: campaign gift';
      } else if (configuredFreeVariantIds.has(Number(item.variant_id))) {
        qualification = 'excluded: configured zero-price gift variant';
      }
      return {
        title: item.product_title || item.title,
        variantId: item.variant_id,
        quantity: item.quantity,
        finalLinePrice: item.final_line_price,
        qualification
      };
    });

    console.groupCollapsed(`[Free gifts] ${label}`);
    console.info('Cart summary', {
      itemCount: cart.item_count,
      cartTotal: cart.total_price,
      qualifyingSubtotal: state.subtotal,
      currency: cart.currency,
      unlockedTiers: state.unlockedTiers.map((tier) => tier.key),
      expectedTiers: state.expectedTiers.map((tier) => tier.key)
    });
    console.table(items);
    console.groupEnd();
  }

  describeError(error, tier = {}) {
    const descriptions = {
      threshold: 'Threshold must be greater than zero.',
      product: 'No gift product is selected.',
      variant_count: tier.setup === 'single_variant'
        ? 'Product only mode requires exactly one variant.'
        : 'Free + paid mode requires exactly two variants.',
      free_variant: 'The zero-price gift variant could not be resolved.',
      paid_variant: 'The regular paid variant could not be resolved.',
      matching_variants: 'Free and paid variant IDs must be different.',
      free_variant_price: 'The gift variant price must be 0. A Shopify theme cannot override a variant price.',
      paid_variant_price: 'The regular paid variant price must be greater than 0.',
      duplicate_free_variant: 'The same free variant is configured in another tier.'
    };
    return descriptions[error] || error;
  }

  thresholdFor(tier, cart) {
    const shopCurrency = this.configuration.shopCurrency;
    const cartCurrency = cart.currency;
    if (!shopCurrency || !cartCurrency || shopCurrency === cartCurrency) return tier.thresholdCents;

    const rate = Number(window.Shopify?.currency?.rate);
    if (Number.isFinite(rate) && rate > 0) return Math.round(tier.thresholdCents * rate);

    this.debug('No Shopify currency rate was available; using the configured threshold without conversion.');
    return tier.thresholdCents;
  }

  getState(cart) {
    const subtotal = this.calculateQualifyingSubtotal(cart);
    const validTiers = this.tiers.filter((tier) => tier.valid);
    const unlockedTiers = validTiers.filter((tier) => subtotal >= this.thresholdFor(tier, cart));
    const mode = this.configuration.mode === 'highest' ? 'highest' : 'cumulative';
    const expectedTiers = mode === 'highest' && unlockedTiers.length
      ? [unlockedTiers.reduce((highest, tier) =>
        this.thresholdFor(tier, cart) >= this.thresholdFor(highest, cart) ? tier : highest)]
      : unlockedTiers;

    return { subtotal, validTiers, unlockedTiers, expectedTiers };
  }

  reconcile(cart = null) {
    if (this.reconciliation) return this.reconciliation;

    this.reconciliation = this.performReconciliation(cart)
      .finally(() => { this.reconciliation = null; });

    return this.reconciliation;
  }

  async performReconciliation(cart) {
    let authoritativeCart = cart || await this.cartAPI.fetchCart();
    let changed = false;
    let state = this.getState(authoritativeCart);
    this.logState('Reconciliation started', authoritativeCart, state);
    const configuredTierByVariant = new Map(this.tiers.map((tier) => [tier.freeVariantId, tier]));
    const allConfiguredFreeVariantIds = new Set(
      (this.configuration.tiers || [])
        .filter((tier) => Number(tier.freeVariantPrice) === 0)
        .map((tier) => Number(tier.freeVariantId))
        .filter((id) => id > 0)
    );
    const expectedTierKeys = new Set(state.expectedTiers.map((tier) => tier.key));
    const attributes = authoritativeCart.attributes || {};
    const attributeUpdates = {};

    state.validTiers.forEach((tier) => {
      const declineKey = this.declineAttributeKey(tier.key);
      if (state.subtotal < this.thresholdFor(tier, authoritativeCart) && attributes[declineKey]) {
        attributeUpdates[declineKey] = '';
      }
    });

    if (Object.keys(attributeUpdates).length) {
      authoritativeCart = await this.cartAPI.update({ attributes: attributeUpdates });
      changed = true;
      state = this.getState(authoritativeCart);
    }

    const updates = {};
    const overflowPaidItems = [];
    const validGiftLinesByTier = new Map();

    (authoritativeCart.items || []).forEach((item) => {
      const properties = item.properties || {};
      const variantId = Number(item.variant_id);
      const configuredTier = configuredTierByVariant.get(variantId);
      const isOurCampaignLine = properties._free_gift === 'true' &&
        properties._gift_campaign === this.configuration.campaignKey;
      const propertyTierKey = String(properties._gift_tier || '');
      const isProperLine = Boolean(
        configuredTier &&
        configuredTier.valid &&
        isOurCampaignLine &&
        propertyTierKey === configuredTier.key
      );
      const isDeclined = Boolean((authoritativeCart.attributes || {})[this.declineAttributeKey(propertyTierKey)]);
      const shouldRemain = this.configuration.enabled && isProperLine &&
        expectedTierKeys.has(propertyTierKey) && !isDeclined;

      if ((allConfiguredFreeVariantIds.has(variantId) || isOurCampaignLine) && !shouldRemain) {
        updates[item.key] = 0;
        return;
      }

      if (shouldRemain) {
        if (validGiftLinesByTier.has(propertyTierKey)) {
          updates[item.key] = 0;
          return;
        }

        validGiftLinesByTier.set(propertyTierKey, item);
        if (Number(item.quantity) !== 1) {
          updates[item.key] = 1;
          const overflowQuantity = Math.max(0, Number(item.quantity) - 1);
          if (
            overflowQuantity > 0 &&
            Number(configuredTier.paidVariantId) > 0 &&
            Number(configuredTier.paidVariantPrice) > 0 &&
            configuredTier.paidAvailable !== false
          ) {
            overflowPaidItems.push({
              id: configuredTier.paidVariantId,
              quantity: overflowQuantity,
              properties: {
                _gift_additional: 'true',
                _gift_tier: configuredTier.key,
                _gift_campaign: this.configuration.campaignKey
              }
            });
          }
        }
      }
    });

    if (Object.keys(updates).length) {
      authoritativeCart = await this.cartAPI.update({ updates });
      changed = true;
      state = this.getState(authoritativeCart);
    }

    if (overflowPaidItems.length) {
      try {
        this.debug('Moving additional gift quantities to paid cart lines.', overflowPaidItems);
        await this.cartAPI.addItems(overflowPaidItems);
        authoritativeCart = await this.cartAPI.fetchCart();
        changed = true;
        state = this.getState(authoritativeCart);
      } catch (error) {
        this.debug('Additional gift quantities could not be migrated to paid cart lines.', error);
      }
    }

    if (this.configuration.enabled && this.configuration.autoAdd) {
      const refreshedGiftTierKeys = new Set(
        (authoritativeCart.items || [])
          .filter((item) => item.properties?._free_gift === 'true' &&
            item.properties?._gift_campaign === this.configuration.campaignKey)
          .map((item) => String(item.properties._gift_tier || ''))
      );

      for (const tier of state.expectedTiers) {
        const declined = Boolean((authoritativeCart.attributes || {})[this.declineAttributeKey(tier.key)]);
        if (declined || refreshedGiftTierKeys.has(tier.key) || this.unavailableVariantIds.has(tier.freeVariantId)) continue;
        if (tier.available === false) {
          this.unavailableVariantIds.add(tier.freeVariantId);
          continue;
        }

        try {
          this.debug(`Adding gift tier ${tier.key}.`, { variantId: tier.freeVariantId, threshold: this.thresholdFor(tier, authoritativeCart) });
          const properties = {
            _free_gift: 'true',
            _gift_tier: tier.key,
            _gift_campaign: this.configuration.campaignKey
          };
          if (this.configuration.debug && tier.freeVariantPrice > 0) properties._gift_debug_paid = 'true';
          await this.cartAPI.addItems([{
            id: tier.freeVariantId,
            quantity: 1,
            properties
          }]);
          refreshedGiftTierKeys.add(tier.key);
          changed = true;
          this.debug(`Gift tier ${tier.key} was added successfully.`);
        } catch (error) {
          this.unavailableVariantIds.add(tier.freeVariantId);
          this.debug(`Gift tier ${tier.key} could not be added.`, error);
        }
      }

      if (changed) authoritativeCart = await this.cartAPI.fetchCart();
    }

    const detail = {
      cart: authoritativeCart,
      changed,
      state: this.getState(authoritativeCart),
      unavailableVariantIds: [...this.unavailableVariantIds]
    };
    this.logState('Reconciliation completed', authoritativeCart, detail.state);
    document.dispatchEvent(new CustomEvent('free-gift:synchronized', { detail }));
    return detail;
  }

  declineAttributeKey(tierKey) {
    return `_free_gift_declined_${this.configuration.campaignKey}_${tierKey}`;
  }

  debug(...args) {
    if (this.configuration.debug) console.info('[Free gifts]', ...args);
  }
}

window.FreeGiftController = FreeGiftController;
